import { DEFAULT_LOCALE, pickPrimary, toCivilDate } from '@mfw/common';

import type {
  ApiOrganizationMember,
  UiOrganizationMember,
} from '../../schemas/organizationUser/organization.member.schema';
import type { UpdateOrganizationMemberArgs } from '../types/organizationMembers.types';
import type { UiUserUpdate } from '../../schemas/users.schema';
import type { Locale } from '../../../../@mfw-translations';

import {
  mergeDefaultSocialMedia,
  mapSocialMediaFromApi,
} from './socialMedia.mappers';
import { mapAddressFromApi } from './address.mappers';
import { mapStatusFromApi } from './status.mappers';
import { getTranslation } from '../../utils';

export function mapOrganizationMemberFromApi(
  member?: ApiOrganizationMember,
): UiOrganizationMember | null {
  if (!member) return null;

  const primaryAddress = pickPrimary(member?.addresses);
  const primaryContact = pickPrimary(member?.contacts);
  const mappedAddress = primaryAddress
    ? mapAddressFromApi(primaryAddress)
    : undefined;

  return {
    id: member.id,
    address: mappedAddress
      ? {
          city: mappedAddress.city,
          country: [mappedAddress.country[0]],
          isAddressPublic: mappedAddress.isAddressPublic,
          state: undefined,
          street: mappedAddress.street,
          zip: mappedAddress.zip,
        }
      : null,
    clientCount: member.clientCount ?? 0,
    connectedAt: member.connectedAt,
    contact: primaryContact ?? null,
    createdAt: toCivilDate(new Date(member.createdAt)),
    email: member.email,
    hasFrontDesk: (member.roles ?? []).some(
      (role) => role.key === 'front_desk',
    ),
    name: member.name,
    orgType: member.orgType ?? null,
    orgUserId: member.orgUserId,
    profileUrl: member.settings?.profileUrl ?? undefined,
    roleIds: (member.roles || []).map((role) => ({
      label: getTranslation(
        (member.settings?.locale as Locale) ?? DEFAULT_LOCALE,
        role.translations,
      ),
      value: role.id,
    })),
    socialMedia: mergeDefaultSocialMedia(
      member.socialMedia?.map(mapSocialMediaFromApi) || [],
    ),
    status: member.orgStatus
      ? mapStatusFromApi(member.orgStatus)
      : mapStatusFromApi(member.status),
    updatedAt: member.updatedAt,
  };
}

export function createUpdateOrganizationMemberArgs(
  values: UiUserUpdate,
): Pick<
  UpdateOrganizationMemberArgs,
  'address' | 'contact' | 'name' | 'socialMedia'
> {
  return {
    address: values.address
      ? {
          city: values.address.city ?? undefined,
          country: values.address.country?.[0]?.value,
          isAddressPublic: values.address.isAddressPublic ?? undefined,
          street: values.address.street ?? undefined,
          zip: values.address.zip ?? undefined,
        }
      : undefined,
    contact:
      values.contact?.phoneCountryCode && values.contact?.phoneNumber
        ? {
            isPhoneNumberPublic:
              values.contact.isPhoneNumberPublic ?? undefined,
            phoneCountryCode: values.contact.phoneCountryCode,
            phoneNumber: values.contact.phoneNumber,
          }
        : undefined,
    name: values.name,
    socialMedia: values.socialMedia
      ? mergeDefaultSocialMedia(values.socialMedia).map((socialMedia) => ({
          type: socialMedia.type,
          url: socialMedia.url ?? undefined,
        }))
      : undefined,
  };
}
